import process from 'node:process';
import { hasFlag, getArgValue } from '../auth.js';
import { apiRequest, fetchList } from '../cli-helpers.js';
import { armPayload, parseAbArgs } from './args.js';
import { printBenchmarkHelp } from './help.js';

type BenchmarkOrgWorkflow = { id: string; name: string };

export async function handleWorkflows(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const workflows = await fetchList<BenchmarkOrgWorkflow>(
    apiUrl,
    token,
    '/superadmin/benchmark/ab-workflows',
    'workflows'
  );
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(workflows, null, 2));
    return;
  }
  if (workflows.length === 0) {
    console.log('No benchmark-org workflows found. Import one with `benchmark import <workflowId>`.');
    return;
  }
  console.log(`Benchmark-org workflows (${workflows.length}):`);
  for (const w of workflows) console.log(`  ${w.id}  ${w.name}`);
}

type BenchmarkRunRow = {
  id: string;
  status: string;
  triggerType?: string;
  startedAt?: string | null;
  completedAt?: string | null;
};

export async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const res = await apiRequest<{ ok: boolean; error?: string; data?: BenchmarkRunRow[] }>(
    apiUrl,
    token,
    'GET',
    '/superadmin/benchmark/workflow-suites'
  );
  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }
  const rows = res.data ?? [];
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(rows, null, 2));
    return;
  }
  if (rows.length === 0) {
    console.log('No benchmark suite runs found.');
    return;
  }
  console.log(`Recent benchmark suite runs (${rows.length}):`);
  for (const r of rows) {
    console.log(`  ${r.id}  [${r.status}]  ${r.triggerType ?? ''}  started=${r.startedAt ?? '—'}`);
  }
}

export async function handleAb(argv: string[], apiUrl: string, token: string): Promise<void> {
  const parsed = parseAbArgs(argv);
  if (!parsed.ok) {
    console.error(`Error: ${parsed.error}`);
    printBenchmarkHelp();
    process.exit(1);
  }
  const { workflowIds, all, runsPerArm, arms, json } = parsed.value;

  // Resolve --all to the full benchmark-org workflow list.
  let ids = workflowIds;
  if (all) {
    const workflows = await fetchList<BenchmarkOrgWorkflow>(
      apiUrl,
      token,
      '/superadmin/benchmark/ab-workflows',
      'workflows'
    );
    ids = workflows.map((w) => w.id);
    if (ids.length === 0) {
      console.error('Error: --all matched no benchmark-org workflows. Import some first.');
      process.exit(1);
    }
  }

  const body: Record<string, unknown> = {
    workflowIds: ids,
    arms: arms.map(armPayload),
    ...(runsPerArm !== undefined ? { runsPerArm } : {}),
  };

  const res = await apiRequest<{ ok: boolean; error?: string; jobId?: string }>(
    apiUrl,
    token,
    'POST',
    '/superadmin/benchmark/ad-hoc',
    body
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }
  if (json) {
    console.log(JSON.stringify(res, null, 2));
    return;
  }
  console.log('Launched ad-hoc benchmark A/B run.');
  console.log(`  jobId:       ${res.jobId}`);
  console.log(`  workflows:   ${ids.length}`);
  console.log(`  runsPerArm:  ${runsPerArm ?? '(server default)'}`);
  console.log(`  arms:        ${arms.map((a) => a.label).join(', ')}`);
  console.log('Watch results with: cofactor benchmark list   (then: benchmark report <suiteId>)');
}

export async function handleImport(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = getArgValue(argv, '--workflow-id') ?? argv.find((a) => !a.startsWith('--'));
  if (!workflowId) {
    console.error('Error: benchmark import <workflowId>');
    process.exit(1);
  }
  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    '/superadmin/benchmark/import-workflow',
    { workflowId }
  );
  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }
  console.log(`Imported workflow ${workflowId} into the benchmark org.`);
}
